'use client'

import React, { FC, ReactElement } from 'react'
import {
	NETWORK_CONFIG,
	API_URL,
	COLLECTIONS_VERSION,
	DEFAULT_FEED_FILTER
} from '@/utils/constants'
import Loading from '@/components/circle-loading'
import ErrorBoundary from '@/components/error-boundary'
import InfiniteScroll from 'react-infinite-scroll-component'
import CollectionCard from '@/components/collection-card'
import ItemGrid from '@/components/item-grid'
import useSWR from 'swr'
import { collectionsFetcher } from './fetcher'
import FeedHeader from '@/components/feed-header'
import CollectionRow from '@/components/collection-row'
import CollectionIcon from '@/components/collection-icon'
import BitcoinIcon from '@/icons/bitcoin-filled'
import RelativeVolumeBar from '@/components/relative-volume-bar'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import { indexedSaleChange, marketWorth } from '@/utils/market-metrics'
import isFungible from '@/utils/is-fungible'
import Link from 'next/link'
import classNames from 'classnames'
import { Tab } from '@/components/tabs'
import useDebounce from '@/utils/hooks/useDebounce'
import FeedFooter from '@/components/feed-footer'
import { DropdownFilter } from '@/utils/types/feed'
import useSWRInfinite from 'swr/infinite'
import AlkanesAmmTable, { ALKANES_AMM_SORTS } from '@/app/tokens/alkanes-amm-table'
import { useRouter, useSearchParams } from 'next/navigation'
import featureFlags from '@/utils/feature-flags'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TableHeader
} from '@/components/ui/table'

const INFINITE_LIMIT = 100

function collectionsUrl(search: string, selected: string, category: string, limit: number, offset = 0) {
	const query = new URLSearchParams({ order: selected, limit: String(limit), offset: String(offset) })
	if (category === 'collections') query.set('nft', 'true')
	if (search || category.includes('-')) query.set('search', search || category)
	return `${API_URL}/${COLLECTIONS_VERSION}?${query}`
}

function useCollectionsLimit(search: string, selected: string, category: string, enabled: boolean) {
	const result = useSWR(enabled ? collectionsUrl(search, selected, category, 16) : null, collectionsFetcher)
	return { ...result, data: result.data?.collections, size: 0, setSize: async (_size: number) => {}, hasMore: false }
}

function useCollectionsInfinite(search: string, selected: string, category: string, enabled: boolean) {
	const result = useSWRInfinite(
		(index, previous) => {
			if (!enabled || (previous && previous.collections.length < INFINITE_LIMIT)) return null
			return collectionsUrl(search, selected, category, INFINITE_LIMIT, index * INFINITE_LIMIT)
		},
		collectionsFetcher,
		{ initialSize: 1, revalidateFirstPage: false }
	)
	const pages = result.data
	return {
		...result,
		data: pages?.flatMap(page => page.collections),
		hasMore: Boolean(pages?.length && pages[pages.length - 1].collections.length === INFINITE_LIMIT)
	}
}

type CollectionsFeedProps = {
	tabs?: Tab[]
	items: any[]
	title: string
	isMobile?: boolean
	className?: string
	sns?: boolean
	infinite?: boolean
	deferUntilVisible?: boolean
	defaultSort?: string
	category?: string
	rightRail?: React.ReactNode
}

const CollectionsFeedInner: FC<CollectionsFeedProps> = (props): ReactElement => {
	const searchParams = useSearchParams()
	const router = useRouter()
	const { exchangeRate } = useExchangeRate()

	const { className, isMobile: _isMobile, title, tabs: _tabs, sns, infinite } = props
	const [search, _setSearch] = React.useState('')
	const selected = searchParams?.get('s') || props.defaultSort || DEFAULT_FEED_FILTER
	const category = props.category || searchParams?.get('c') || 'all'
	const [gridView, setGridView] = React.useState(false)
 const feedRef = React.useRef<HTMLDivElement>(null)
 const [nearViewport, setNearViewport] = React.useState(!props.deferUntilVisible)
 React.useEffect(() => {
  if (!props.deferUntilVisible || nearViewport) return
  const node = feedRef.current
  if (!node) return
  const observer = new IntersectionObserver(entries => {
   if (entries.some(entry => entry.isIntersecting)) { setNearViewport(true); observer.disconnect() }
  }, { rootMargin: '600px' })
  observer.observe(node)
  return () => observer.disconnect()
 }, [props.deferUntilVisible, nearViewport])


	const debouncedValue: string = useDebounce<string>(search, 300)
	const shouldFetchLimitedCollections =
		Boolean(debouncedValue) ||
		selected !== (props.defaultSort || DEFAULT_FEED_FILTER) ||
		category !== (props.category || 'all')

	// Alkanes trade on the AMM: the marketplace feed has no listings, floor or
	// volume for them, so that category shows the AMM markets instead and the
	// marketplace queries stay off.
	const amm = category === 'alkane-'
	const [ammSort, setAmmSort] = React.useState<string>('volume_24h_usd')
	const ammSortOptions = Object.entries(ALKANES_AMM_SORTS).map(([id, title]) => ({
		id,
		title,
		onClick: () => setAmmSort(id)
	}))
	const limitedCollections = useCollectionsLimit(
		debouncedValue,
		selected,
		category,
		!amm && !infinite && nearViewport && shouldFetchLimitedCollections
	)
	const infiniteCollections = useCollectionsInfinite(debouncedValue, selected, category, !amm && Boolean(infinite) && nearViewport)
	const { data, isLoading, isValidating, error, mutate, size, setSize, hasMore } = infinite ? infiniteCollections : limitedCollections
	// Server-provided rows only belong to the initial filter. An empty response
	// is a valid result, not a reason to show unrelated initial rows again.

	// Smart sorting: items with volume in selected period first, then items with 0 volume sorted by all-time
	const items = React.useMemo(() => {
		const rawItems: any[] = data ?? (shouldFetchLimitedCollections ? [] : props.items)
		if (!rawItems?.length) return rawItems

		// Get the volume key for the selected filter
		const volumeKeyMap: Record<string, string> = {
			VolumeTotalDesc: 'volume_all_time',
			VolumeMonthDesc: 'volume_month',
			VolumeWeekDesc: 'volume_week',
			VolumeDayDesc: 'volume_day',
			GlobalVolumeTotalDesc: 'global_volume_all_time',
			GlobalVolumeWeekDesc: 'global_volume_week',
			GlobalVolumeDayDesc: 'global_volume_day',
			PriceChangeDayDesc: 'price_change_day',
			ChangeWeekDesc: 'change_week'
		}

		const volumeKey = volumeKeyMap[selected] || 'volume_all_time'

		// For change-based filters, total volume, or any global-volume sort, trust
		// the API order (global_volume_* are indexed + sorted server-side).
		if (
			selected === 'VolumeTotalDesc' ||
			selected.includes('Change') ||
			selected.startsWith('GlobalVolume')
		) {
			return rawItems
		}
		
		// Zero OW volume does not mean an asset is absent or inactive elsewhere.
		// Keep every result, ranking period volume first and all-time volume next.
		return [...rawItems].sort((a, b) =>
			((b?.[volumeKey] || 0) - (a?.[volumeKey] || 0)) ||
			((b?.volume_all_time || 0) - (a?.volume_all_time || 0))
		)

	}, [data, shouldFetchLimitedCollections, props.items, selected])

	const updateFilters = (nextCategory: string, nextSort: string) => {
		const url = new URL(window.location.href)
		url.searchParams.set('c', nextCategory)
		url.searchParams.set('s', nextSort)
		// These filters are client data: update Next's search params without
		// reloading the entire homepage or moving the user's scroll position.
		window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
	}
	const setFilter = (key: string) => updateFilters(category, key)
	const setCategoryFilter = (key: string) => updateFilters(key, selected)

	const filters: DropdownFilter[] = [
		{
			title: 'Volume 24h',
			id: 'VolumeDayDesc',
			onClick: () => setFilter('VolumeDayDesc')
		},
		{
			title: 'Volume 7d',
			id: 'VolumeWeekDesc',
			onClick: () => setFilter('VolumeWeekDesc')
		},
		{
			title: 'Volume 30d',
			id: 'VolumeMonthDesc',
			onClick: () => setFilter('VolumeMonthDesc')
		},
		{
			title: 'Total Volume',
			id: 'VolumeTotalDesc',
			onClick: () => setFilter('VolumeTotalDesc')
		},
		{
			title: 'Global Vol 24h',
			id: 'GlobalVolumeDayDesc',
			onClick: () => setFilter('GlobalVolumeDayDesc')
		},
		{
			title: 'Global Vol 7d',
			id: 'GlobalVolumeWeekDesc',
			onClick: () => setFilter('GlobalVolumeWeekDesc')
		},
		{
			title: 'Global Vol Total',
			id: 'GlobalVolumeTotalDesc',
			onClick: () => setFilter('GlobalVolumeTotalDesc')
		},
		{
			title: 'Change 24h',
			id: 'PriceChangeDayDesc',
			onClick: () => setFilter('PriceChangeDayDesc')
		},
		{
			title: 'Volume growth 7d',
			id: 'ChangeWeekDesc',
			onClick: () => setFilter('ChangeWeekDesc')
		}
	]
	const brc20Protocol = NETWORK_CONFIG?.brc20Protocol ?? 'brc-20'

	const categories = [
		{
			title: 'All',
			id: 'all',
			onClick: () => setCategoryFilter('all')
		},
		{
			title: 'Collections',
			id: 'collections',
			onClick: () => setCategoryFilter('collections')
		},
		!featureFlags('app::disable_runes') && {
			title: 'Runes',
			id: 'rune-',
			onClick: () => setCategoryFilter('rune-')
		},
		{
			title: 'Alkanes',
			id: 'alkane-',
			onClick: () => setCategoryFilter('alkane-')
		},
		{
			title: brc20Protocol.replace('-', '').toUpperCase(),
			id: `${brc20Protocol.replace('-', '')}-`,
			onClick: () => setCategoryFilter(`${brc20Protocol.replace('-', '')}-`)
		},
		!featureFlags('app::disable_tap') && {
			title: 'TAP',
			id: 'tap-',
			onClick: () => setCategoryFilter('tap-')
		},
		!featureFlags('app::disable_sns') && {
			title: 'SNS',
			id: '-names',
			onClick: () => setCategoryFilter('-names')
		},
		!featureFlags('app::disable_brc420') && {
			title: 'BRC420',
			id: 'brc420-',
			onClick: () => setCategoryFilter('brc420-')
		}
	].filter((e): e is DropdownFilter => Boolean(e))

	const filter = filters.find((e) => e.id === selected) || filters[1]

	const columnHeaders = [
		{ label: '#' },
		{ label: 'Collection' },
		{
			label: ['VolumeWeekDesc', 'GlobalVolumeWeekDesc', 'OwnersDesc', 'VolumeTotalDesc'].includes(selected)
				? '7D sale change'
				: filter?.title?.toUpperCase()
		},
		{ label: `Global Vol ${selected.includes('Week') ? '7D' : selected.includes('Day') ? '24H' : 'All time'}` },
		{ label: `OW Vol ${selected.includes('Week') ? '7D' : selected.includes('Day') ? '24H' : 'All time'}` },
		{ label: 'Price' }
	].map((header) => ({
		...header,
		label: (header as any).label.toUpperCase()
	}))

	const renderGridView = () => (
			<ItemGrid
				className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
				isLoading={isLoading}
			>
			{items?.map((e, i) => (
				<CollectionCard key={e.slug || i} collection={e} />
			))}
		</ItemGrid>
	)

	const renderItemTable = () => {
		const key = (filter as any).id
		const isHomeLeaderboard = className?.includes('home-leaderboard')
		const btcValue = (sats: number) => sats > 0 ? (sats / 1e8).toLocaleString('en-US', { minimumFractionDigits: sats < 1e6 ? 4 : 2, maximumFractionDigits: sats < 1e6 ? 6 : 2 }) : '—'
		const usdValue = (sats: number) => exchangeRate && sats > 0 ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(sats / 1e8 * exchangeRate) : '—'
		const highestGlobalVolume = items?.reduce((highest, collection) => Math.max(highest, Number(collection.global_volume_week ?? collection.volume_week) || 0), 0) ?? 0

		return (
			<div
				className={classNames(
					'w-full',
					!className?.includes('rounded-none') && 'rounded-md',
					className?.includes('border-x-0') && 'border-x-0'
				)}
			>
				{isHomeLeaderboard && <div className="w-full overflow-x-auto">
					<table className="mobile-market-table w-full min-w-[740px] table-fixed text-left text-xs md:min-w-[900px]">
						<thead><tr className="border-y border-border/70 text-[10px] text-muted-foreground"><th className="w-[34px] py-3 pl-4">#</th><th className="w-[160px] py-3 pr-2">Collection</th><th className="w-[103px] px-2 py-3">Price</th><th className="w-[115px] px-2 py-3">7d vol</th><th className="w-[188px] px-3 py-3">OW vol</th><th className="w-[140px] px-3 py-3">Relative vol</th></tr></thead>
						<tbody>{items?.map((collection, i) => {
							const floor = marketWorth(collection as any, Boolean(isFungible(collection.slug))) ?? 0
							const globalVolume = Number(collection.global_volume_week ?? collection.volume_week) || 0
							const owVolume = Number(collection.volume_week) || 0
							const change = indexedSaleChange(collection)
							return <tr key={collection.slug || i} className="border-b border-border/50">
								<td className="py-3 pl-4 text-muted-foreground tabular-nums">{i + 1}</td>
								<td className="py-3 pr-2"><Link href={`/collection/${collection.slug}`} className="flex min-w-0 items-center gap-2"><CollectionIcon collection={collection} className="size-7 shrink-0 overflow-hidden rounded-md bg-muted" imageClassName="size-full object-cover" /><span className="min-w-0 truncate font-semibold">{collection.name}</span></Link></td>
								<td className="px-2 py-3 tabular-nums"><span className="flex items-center gap-0.5 whitespace-nowrap font-semibold"><BitcoinIcon className="size-3 shrink-0 text-orange-500" aria-hidden="true" />{btcValue(floor)}</span><span className={`block text-[10px] ${change == null || change === 0 ? 'text-muted-foreground' : change > 0 ? 'text-emerald-500' : 'text-rose-500'}`}>{change == null ? '—' : `${change < 0 ? '-' : ''}${Math.abs(change).toFixed(2)}%`}</span></td>
								<td className="px-2 py-3 tabular-nums"><span className="flex items-center gap-0.5 whitespace-nowrap font-semibold"><BitcoinIcon className="size-3 shrink-0 text-orange-500" aria-hidden="true" />{btcValue(globalVolume)}</span><span className="block text-[10px] text-muted-foreground">{usdValue(globalVolume)}</span></td>
								<td className="px-3 py-3 tabular-nums"><span className="flex items-center gap-0.5 whitespace-nowrap"><BitcoinIcon className="size-3 shrink-0 text-orange-500" aria-hidden="true" />{btcValue(owVolume)}</span><span className="block text-[10px] text-muted-foreground">{usdValue(owVolume)}</span></td>
								<td className="px-3 py-3"><RelativeVolumeBar value={globalVolume} max={highestGlobalVolume} /></td>
							</tr>
						})}</tbody>
					</table>
				</div>}
				{!isHomeLeaderboard && <div className="relative w-full overflow-auto [&_tbody]:!border-b-0 [&_tr]:!border-b-0">
					<Table>
						<TableHeader className="border-t border-b border-input">
							<TableRow className="hover:bg-transparent !border-0">
								{columnHeaders.map((header, index) => (
									<TableHead
										key={index}
										className={classNames(
											'py-2 text-xs font-medium text-muted-foreground !border-0 whitespace-nowrap',
											index === 0 ? 'w-8 pl-3 pr-0' : '',
											index === 1 ? 'w-[280px]' : '',
											index > 1 ? 'text-right pr-4' : ''
										)}
									>
										{header.label}
									</TableHead>
								))}
							</TableRow>
						</TableHeader>
						<TableBody>
							{isLoading && !items?.length ? (
								<TableRow>
									<TableCell colSpan={columnHeaders.length} className="h-12 text-center">
										<Loading />
									</TableCell>
								</TableRow>
							) : (
								items?.map((collection, i) => (
									<TableRow
										key={collection.slug || i}
										onClick={() => router.push(`/collection/${collection.slug}`)}
										className="hover:bg-muted/70 transition-colors cursor-pointer"
									>
										<TableCell className="py-3 pl-3 pr-0 text-muted-foreground text-xs w-8">
											{i + 1}
										</TableCell>
										<CollectionRow
											rank={i}
											collection={collection}
											selectedKey={key}
											selectedTitle={(filter as any).title}
										/>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</div>}
			</div>
		)
	}

	return (
		<ErrorBoundary>
			<section ref={feedRef} aria-label={title} className={classNames(className, 'flex flex-col w-full')}>
				<FeedHeader
					title={title}
					mobileTitle={false}
					// Marketplace sorts mean nothing for AMM markets: in the Alkanes
					// category the same "Sort by" control drives the AMM metrics.
					selectedOption={amm ? ammSort : selected}
					options={amm ? ammSortOptions : filters}
					selectedSecondaryOption={props.category ? null : category as any}
					secondaryOptions={props.category ? undefined : categories}
					gridView={gridView}
					setGridView={setGridView}
					label={sns ? 'SNS' : 'Collection'}
					divider
				/>
				<div className={props.rightRail ? '2xl:grid 2xl:grid-cols-[minmax(0,1fr)_320px]' : undefined}>
				<div className="min-w-0">
				{amm && <AlkanesAmmTable sortParam={ammSort} onSort={setAmmSort} />}
				{!amm && <>
				{props.deferUntilVisible && <p className="border-t border-border/70 bg-muted/15 px-4 py-2 text-[10px] text-muted-foreground sm:hidden">Swipe for prices and trading volume →</p>}
				{error && (
					<div role="status" className="px-4 pb-4 text-sm text-muted-foreground">
						Could not load these results.{' '}
						<button type="button" className="underline min-h-11" disabled={isValidating}
							onClick={() => void mutate().catch(() => {})}>Retry</button>
					</div>
				)}
				{!isLoading && !error && items.length === 0 && (
					<p role="status" className="px-4 py-6 text-sm text-muted-foreground">No collections found in this category.</p>
				)}
				{!infinite && (
					<>
						{(gridView && renderGridView()) || (!gridView && renderItemTable())}
						<FeedFooter className="mt-8" href={`/collect?tab=${sns ? 'names' : 'nft'}`} />
					</>
				)}
				{infinite && (
					<InfiniteScroll
						dataLength={items?.length}
						next={() => { if (!isValidating && !error) void setSize(size + 1).catch(() => {}) }}
						hasMore={hasMore && !error}
						loader={
							<div className="flex justify-center items-center p-4">
								<Loading />
							</div>
						}
					>
						{(gridView && renderGridView()) || (!gridView && renderItemTable())}
					</InfiniteScroll>
				)}
				</>}
				</div>
				{props.rightRail}
				</div>
			</section>
		</ErrorBoundary>
	)
}

// Wrap in Suspense because CollectionsFeedInner calls useSearchParams() —
// Next.js requires a Suspense boundary around any client component that
// reads search params or pre-renders bail out.
const CollectionsFeed: FC<CollectionsFeedProps> = (props): ReactElement => (
	<React.Suspense fallback={null}>
		<CollectionsFeedInner {...props} />
	</React.Suspense>
)

export default CollectionsFeed
